import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMail, FiLock, FiEye, FiEyeOff, FiLoader } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { extractError } from '../../api/client';
import { toast } from '../../utils/alert';
import { COMPANY_NAME } from '../../config';

const FEATURES = [
  { icon: 'bi bi-bar-chart-fill', title: 'Live Dashboard', desc: 'Sales, receivables & payroll at a glance' },
  { icon: 'bi bi-receipt', title: 'Smart Invoicing', desc: 'Generate & track invoices with payments' },
  { icon: 'bi bi-gear-wide-connected', title: 'Production Tracking', desc: 'Machines, stitches & operators' },
  { icon: 'bi bi-briefcase', title: 'Full Payroll', desc: 'Auto salary calc with deductions' },
];

export default function Login() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.email) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Enter a valid email address';
    if (!form.password) errs.password = 'Password is required';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast('success', '', 'Welcome back!');
    } catch (error) {
      toast('error', '', extractError(error, 'Invalid credentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-left">
        <div className="auth-left-content">
          <div className="auth-brand">
            <img src="/logo.png" alt="Logo" className="logo-badge" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'contain' }} />
            <div>
              <div className="logo-name">{COMPANY_NAME}</div>
              <div className="logo-sub">Embroidery ERP</div>
            </div>
          </div>
          <div className="auth-headline">
            <h1>
              Manage your business,
              <br />
              beautifully.
            </h1>
            <p>
              Complete ERP solution for Ahmed Corporation — accounting, production,
              payroll and inventory in one powerful platform.
            </p>
          </div>
          <div className="auth-features">
            {FEATURES.map((f) => (
              <div key={f.title} className="auth-feature">
                <div className="f-icon"><i className={f.icon} /></div>
                <div>
                  <div className="f-title">{f.title}</div>
                  <div className="f-desc">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="auth-foot">© 2026 {COMPANY_NAME}. All rights reserved.</div>
        <div style={{ textAlign: 'center', fontSize: 11, color: '#fff', marginTop: 6, opacity: 0.7 }}>Designed & Developed by <a href="https://www.odinno.com" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'underline' }}>www.odinno.com</a></div>
      </div>

      <div className="auth-right">
        <div className="auth-card">
          <h2 className="auth-card-title">Welcome back</h2>
          <p className="auth-card-sub">Sign in to your account to continue.</p>

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <div className="auth-input-wrap">
                <FiMail className="input-icon" />
                <input
                  className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  value={form.email}
                  onChange={handleChange}
                />
              </div>
              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div className="auth-input-wrap">
                <FiLock className="input-icon" />
                <input
                  className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                  type={showPass ? 'text' : 'password'}
                  name="password"
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="eye-toggle"
                  onClick={() => setShowPass((v) => !v)}
                  aria-label="Toggle password"
                >
                  {showPass ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {errors.password && <div className="invalid-feedback">{errors.password}</div>}
            </div>

            <div className="auth-options">
              <label>
                <input type="checkbox" defaultChecked /> Remember me
              </label>
              <Link to="/login" className="auth-link">Forgot password?</Link>
            </div>

            <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={loading}>
              {loading ? <><FiLoader className="spin" /> Signing in...</> : 'Sign In'}
            </button>
          </form>
        {/*
          <div className="auth-divider">OR</div>

            <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
            Don't have an account?{' '}
            <Link to="/register" className="auth-link">Create account</Link>
          </div> */}
        </div>
      </div>
    </div>
  );
}